import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Check, X, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { BrandWordmark } from "@/components/BrandWordmark";
import { getComparison, COMPARISONS } from "@/data/comparisons";

function Cell({ value }) {
  if (value === true) return <Check className="h-4 w-4 text-emerald-400 mx-auto" aria-label="Yes" />;
  if (value === false) return <X className="h-4 w-4 text-zinc-600 mx-auto" aria-label="No" />;
  return <span className="text-xs text-zinc-300">{value}</span>;
}

export default function Comparison() {
  const { slug } = useParams();
  const c = getComparison(slug);
  if (!c) return <Navigate to="/compare" replace />;

  const ld = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://www.videoileo.com/" },
          { "@type": "ListItem", position: 2, name: "Compare", item: "https://www.videoileo.com/compare" },
          { "@type": "ListItem", position: 3, name: `Videoileo vs ${c.name}`, item: `https://www.videoileo.com/compare/${c.slug}` },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: c.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="comparison-page">
      <Seo title={c.metaTitle.replace(" | Videoileo", "")} description={c.description} path={`/compare/${c.slug}`} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
      </Helmet>

      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center"><BrandWordmark className="h-12" /></Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-zinc-300 hover:text-white px-3 py-2 transition-colors">Sign in</Link>
            <Link to="/register" data-testid="compare-start-free" className="text-sm bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md font-medium transition-colors">Start free</Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-14">
        <nav className="text-xs text-zinc-500 mb-4" data-testid="compare-breadcrumb">
          <Link to="/" className="hover:text-zinc-300">Home</Link> <span className="mx-1">/</span>
          <Link to="/compare" className="hover:text-zinc-300">Compare</Link> <span className="mx-1">/</span>
          <span className="text-zinc-400">Videoileo vs {c.name}</span>
        </nav>
        <h1 className="font-head text-4xl sm:text-5xl tracking-tighter font-black mb-3">Videoileo vs {c.name}</h1>
        <p className="text-zinc-400 text-lg mb-8">{c.tagline}</p>
        <div className="space-y-4 max-w-3xl mb-12">
          {c.intro.map((p, i) => <p key={i} className="text-zinc-300 leading-relaxed">{p}</p>)}
        </div>

        <div className="border border-white/10 rounded-md overflow-hidden mb-12" data-testid="comparison-table">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#121212] text-xs uppercase tracking-[0.15em] text-zinc-500">
                <th className="px-4 py-3 font-medium">Feature</th>
                <th className="px-4 py-3 font-medium text-center text-blue-400">Videoileo</th>
                <th className="px-4 py-3 font-medium text-center">{c.name}</th>
              </tr>
            </thead>
            <tbody>
              {c.rows.map((r) => (
                <tr key={r.feature} className="border-t border-white/5 odd:bg-white/[0.02]">
                  <td className="px-4 py-3 text-sm text-zinc-300">{r.feature}</td>
                  <td className="px-4 py-3 text-center">{<Cell value={r.us} />}</td>
                  <td className="px-4 py-3 text-center">{<Cell value={r.them} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="border border-white/10 rounded-md bg-[#121212] p-6">
            <h2 className="font-head text-lg font-semibold mb-4">Choose {c.name} if…</h2>
            <ul className="space-y-3">
              {c.chooseThem.map((b, i) => <li key={i} className="text-sm text-zinc-400 leading-relaxed flex gap-2"><span className="text-zinc-600 mt-0.5">→</span>{b}</li>)}
            </ul>
          </div>
          <div className="border border-blue-500/30 rounded-md bg-blue-600/5 p-6">
            <h2 className="font-head text-lg font-semibold mb-4 text-blue-300">Choose Videoileo if…</h2>
            <ul className="space-y-3">
              {c.chooseUs.map((b, i) => <li key={i} className="text-sm text-zinc-300 leading-relaxed flex gap-2"><Check className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />{b}</li>)}
            </ul>
          </div>
        </div>

        <section className="mb-12">
          <h2 className="font-head text-2xl font-bold mb-5">Frequently asked</h2>
          <div className="space-y-4">
            {c.faqs.map((f) => (
              <div key={f.q} className="border border-white/10 rounded-md bg-[#121212] p-5">
                <h3 className="font-semibold text-sm mb-2">{f.q}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="border border-white/10 rounded-md bg-gradient-to-r from-blue-600/15 to-transparent p-8 text-center" data-testid="compare-cta">
          <h2 className="font-head text-2xl font-bold mb-2">Try the faceless channel studio free</h2>
          <p className="text-zinc-400 text-sm mb-5">100 free credits on signup — enough for a couple of full videos. No card required.</p>
          <Link to="/register" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-6 py-3 rounded-md font-medium transition-colors" data-testid="compare-cta-register">
            Start creating free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-10 flex flex-wrap gap-x-4 gap-y-2 text-xs text-zinc-500">
          {COMPARISONS.filter((x) => x.slug !== c.slug).map((x) => (
            <Link key={x.slug} to={`/compare/${x.slug}`} className="hover:text-zinc-300 underline underline-offset-2">Videoileo vs {x.name}</Link>
          ))}
        </div>
        <p className="mt-6 text-[11px] text-zinc-700 max-w-3xl leading-relaxed">
          Comparison based on publicly available information about {c.name}, last reviewed in 2026. Features and pricing change — always confirm details on the vendor's site. {c.name} is a trademark of its respective owner; Videoileo is not affiliated with it.
        </p>
      </main>
    </div>
  );
}
